import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';

const neutral = {
  0: '#F4F4F4',
  50: '#F3F3F3',
  100: '#DDDDDD',
  200: '#BCBEBC',
  300: '#9FA09F',
  400: '#818381',
  500: '#464846',
  600: '#383A38',
  700: '#2A2B2A',
  800: '#1C1D1C',
  900: '#0E0E0E',
  black: '#000000',
  white: '#FFFFFF',
};

const brand = {
  0: '#FCFBE6',
  50: '#F8F5CC',
  100: '#F4F1B3',
  200: '#ECE67F',
  300: '#DACF00',
  700: '#C7BD03',
  900: '#BAB000',
};

const semantic = {
  info: '#0DCAF0',
  infoLight: '#CFF4FC',
  infoDark: '#0AA2C0',
  success: '#198754',
  successLight: '#BFECD7',
  successDark: '#146C43',
  warning: '#FFC107',
  warningLight: '#FFF3CD',
  warningDark: '#CC9A06',
  error: '#DC3545',
  errorLight: '#F8D7DA',
  errorDark: '#B02A37',
};

let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: neutral[900],
      light: neutral[500],
      dark: neutral.black,
      contrastText: neutral.white,
    },
    secondary: {
      main: brand[300],
      light: brand[100],
      dark: brand[900],
      contrastText: neutral[900],
    },
    success: {
      main: semantic.success,
      light: semantic.successLight,
      dark: semantic.successDark,
      contrastText: neutral.white,
    },
    warning: {
      main: semantic.warning,
      light: semantic.warningLight,
      dark: semantic.warningDark,
      contrastText: '#332701',
    },
    error: {
      main: semantic.error,
      light: semantic.errorLight,
      dark: semantic.errorDark,
      contrastText: neutral.white,
    },
    info: {
      main: semantic.info,
      light: semantic.infoLight,
      dark: semantic.infoDark,
      contrastText: '#032830',
    },
    text: {
      primary: neutral[900],
      secondary: neutral[500],
      disabled: neutral[200],
    },
    background: {
      default: neutral[0],
      paper: neutral.white,
    },
    divider: neutral[100],
    grey: {
      50: neutral[0],
      100: neutral[50],
      200: neutral[100],
      300: neutral[200],
      400: neutral[300],
      500: neutral[400],
      600: neutral[500],
      700: neutral[600],
      800: neutral[700],
      900: neutral[900],
    },
    action: {
      hover: alpha(neutral[900], 0.04),
      selected: alpha(brand[300], 0.18),
      disabled: neutral[200],
      disabledBackground: neutral[50],
      focus: alpha(brand[300], 0.2),
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '3rem',
      lineHeight: 70 / 48,
      fontWeight: 800,
      letterSpacing: 0,
    },
    h2: {
      fontSize: '2.5rem',
      lineHeight: 54 / 40,
      fontWeight: 800,
      letterSpacing: 0,
    },
    h3: {
      fontSize: '2rem',
      lineHeight: 44 / 32,
      fontWeight: 700,
      letterSpacing: 0,
    },
    h4: {
      fontSize: '1.375rem',
      lineHeight: 28 / 22,
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    h5: {
      fontSize: '1.375rem',
      lineHeight: 28 / 22,
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    h6: {
      fontSize: '1.125rem',
      lineHeight: 26 / 18,
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1.125rem',
      lineHeight: 24 / 18,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '1rem',
      lineHeight: 22 / 16,
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 22 / 16,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 20 / 14,
      fontWeight: 400,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 18 / 12,
      fontWeight: 500,
    },
    button: {
      fontSize: '0.875rem',
      lineHeight: 20 / 14,
      fontWeight: 600,
      letterSpacing: '0em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  customShadows: {
    xs: '0px 1px 2px rgba(14, 14, 14, 0.06)',
    sm: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    md: '0px 6px 15px rgba(16, 24, 40, 0.08)',
    lg: '0px 14px 30px rgba(14, 14, 14, 0.1)',
    xl: '0px 18px 38px rgba(14, 14, 14, 0.12)',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: neutral[0],
          color: neutral[900],
        },
        '*': {
          boxSizing: 'border-box',
        },
        '::selection': {
          backgroundColor: alpha(brand[300], 0.4),
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // Visible keyboard focus ring on every interactive surface
          // (buttons, icon buttons, list items, tabs, menu items). Uses the
          // info colour so it stays visible on both light and dark backgrounds.
          '&.Mui-focusVisible': {
            outline: `2px solid ${semantic.info}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: neutral.white,
          color: neutral[900],
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderBottom: 'none',
          backdropFilter: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: neutral.white,
          color: neutral[900],
          borderRight: `1px solid ${neutral[100]}`,
          boxShadow: '0px 6px 15px rgba(16, 24, 40, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${neutral[100]}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${neutral[100]}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${neutral[100]}`,
          boxShadow: '0px 18px 42px rgba(14, 14, 14, 0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px 12px',
          color: neutral[900],
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '12px 24px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0 24px 24px',
          gap: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 38,
          padding: '8px 14px',
          borderRadius: 8,
          boxShadow: 'none',
        },
        sizeLarge: {
          minHeight: 42,
          padding: '10px 16px',
        },
        containedPrimary: {
          backgroundColor: neutral[900],
          color: neutral.white,
          '&:hover': {
            backgroundColor: neutral.black,
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: brand[300],
          color: neutral[900],
          '&:hover': {
            backgroundColor: brand[700],
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: 1,
          borderColor: neutral[100],
          '&:hover': {
            borderWidth: 1,
            borderColor: alpha(neutral[900], 0.2),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 26,
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
        outlined: {
          borderColor: neutral[100],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: neutral.white,
          '& fieldset': {
            borderColor: neutral[100],
          },
          '&:hover fieldset': {
            borderColor: alpha(neutral[900], 0.2),
          },
          '&.Mui-focused fieldset': {
            borderColor: neutral[900],
            borderWidth: 1,
          },
          '&.Mui-disabled': {
            backgroundColor: alpha(neutral[50], 0.45),
          },
        },
        input: {
          paddingTop: 10.5,
          paddingBottom: 10.5,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          color: neutral[400],
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: neutral[400],
          fontWeight: 500,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: neutral[500],
          '&.Mui-checked': {
            color: neutral[900],
          },
          '&.Mui-disabled': {
            color: neutral[200],
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: neutral[100],
          '&.Mui-checked': {
            color: neutral[900],
          },
          '&.Mui-disabled': {
            color: neutral[100],
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: neutral.white,
          '&.Mui-checked': {
            color: neutral.white,
            '& + .MuiSwitch-track': {
              backgroundColor: semantic.success,
              opacity: 1,
            },
          },
        },
        track: {
          backgroundColor: neutral[100],
          opacity: 1,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[900],
          color: brand[300],
          fontWeight: 700,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${neutral[50]}`,
          color: neutral[700],
          fontSize: '0.875rem',
          lineHeight: '18px',
          padding: '12px',
        },
        head: {
          color: neutral[900],
          fontSize: '1rem',
          fontWeight: 500,
          lineHeight: '22px',
          backgroundColor: neutral[0],
          borderBottom: `1px solid ${neutral[100]}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: 0,
          minHeight: 42,
          padding: 12,
          color: neutral[500],
          '& .MuiListItemIcon-root': {
            color: 'inherit',
            minWidth: 24,
          },
          '&.Mui-selected': {
            backgroundColor: brand[0],
            color: neutral[900],
          },
          '&.Mui-selected:hover': {
            backgroundColor: brand[0],
          },
          '&:hover': {
            backgroundColor: brand[0],
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${neutral[100]}`,
          boxShadow: '0px 12px 28px rgba(14, 14, 14, 0.12)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
        indicator: {
          height: 2,
          borderRadius: 2,
          backgroundColor: neutral[900],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          minWidth: 0,
          padding: '10px 12px',
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: neutral[400],
          alignItems: 'flex-start',
          '&.Mui-selected': {
            color: neutral[900],
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${neutral[100]}`,
          borderRadius: 8,
          backgroundColor: neutral.white,
          color: neutral[700],
          fontSize: '0.9375rem',
          lineHeight: '20px',
        },
        columnHeaders: {
          backgroundColor: neutral[0],
          borderBottom: `1px solid ${neutral[100]}`,
        },
        columnHeaderTitle: {
          color: neutral[900],
          fontWeight: 500,
          fontSize: '1rem',
          lineHeight: '22px',
        },
        cell: {
          borderBottom: `1px solid ${neutral[50]}`,
          alignItems: 'center',
          fontSize: '0.9375rem',
          lineHeight: '20px',
          paddingTop: 10,
          paddingBottom: 10,
        },
        row: {
          '&:hover': {
            backgroundColor: alpha(brand[0], 0.75),
          },
        },
        footerContainer: {
          borderTop: `1px solid ${neutral[100]}`,
          minHeight: 52,
        },
        columnSeparator: {
          display: 'none',
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

export default theme;
